import React, { memo, useMemo } from "react";

function parseCentroid(cordStr) {
  const pts = cordStr.trim().split(/\s+/).map((p) => p.split(",").map(Number));
  return [
    (pts[0][0] + pts[1][0] + pts[2][0]) / 3,
    (pts[0][1] + pts[1][1] + pts[2][1]) / 3,
  ];
}

function parsePts(str) {
  return str.trim().split(/\s+/).map((p) => p.split(",").map(Number));
}

function dist2(a, b) {
  return (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
}

/* ── single chevron stream: all arrows start at `from`, staggered in time ── */
function ArrowStream({ from, to, color, chevronSize }) {
  const dx    = to[0] - from[0];
  const dy    = to[1] - from[1];
  const angle = Math.atan2(dy, dx);
  const cos   = Math.cos(angle);
  const sin   = Math.sin(angle);

  const NUM    = 3;
  const DUR    = 1.4;
  const CS     = chevronSize;
  const SW     = Math.max(1.5, CS * 0.3);

  // Bake rotation into the chevron points so we only need a pure translate
  const rp = (px, py) => [
    from[0] + px * cos - py * sin,
    from[1] + px * sin + py * cos,
  ];

  const [ax, ay] = rp(-CS, -CS * 0.58);
  const [bx, by] = rp(0,   0);
  const [ex, ey] = rp(-CS,  CS * 0.58);
  const ptsStr   = `${ax},${ay} ${bx},${by} ${ex},${ey}`;

  return (
    <>
      {Array.from({ length: NUM }, (_, i) => {
        const begin  = `${-(i / NUM) * DUR}s`;
        const stroke = color;

        return (
          <polyline
            key={i}
            points={ptsStr}
            fill="none"
            stroke={stroke}
            strokeWidth={SW}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0}
          >
            <animateTransform
              attributeName="transform"
              type="translate"
              from="0 0"
              to={`${dx} ${dy}`}
              dur={`${DUR}s`}
              begin={begin}
              repeatCount="indefinite"
              calcMode="linear"
            />
            <animate
              attributeName="opacity"
              values="0;0;1;1;0;0"
              keyTimes="0;0.05;0.2;0.88;0.97;1"
              dur={`${DUR}s`}
              begin={begin}
              repeatCount="indefinite"
            />
          </polyline>
        );
      })}
    </>
  );
}

/* ── arrows in the gap between outer and inner home triangles ── */
function TurnArrows({ outerCordStr, innerCordStr, boardCenter, color }) {
  const outerPts = parsePts(outerCordStr);
  const innerPts = parsePts(innerCordStr);

  const splitTipBases = (pts) => {
    const tipIdx = pts.reduce(
      (mi, p, i, arr) => (dist2(p, boardCenter) < dist2(arr[mi], boardCenter) ? i : mi),
      0
    );
    return { tip: pts[tipIdx], bases: pts.filter((_, i) => i !== tipIdx) };
  };

  const { tip: outerTip, bases: outerBases } = splitTipBases(outerPts);
  const { tip: innerTip, bases: innerBases } = splitTipBases(innerPts);

  // For each inner base, find the closest outer base
  const pairs = innerBases.map((ib) => ({
    ib,
    ob: outerBases.reduce((best, ob) => (dist2(ib, ob) < dist2(ib, best) ? ob : best)),
  }));

  // Arrow paths run in the gap midline: midpoint(outerBase, innerBase) → midpoint(outerTip, innerTip)
  const tipMid = [(outerTip[0] + innerTip[0]) / 2, (outerTip[1] + innerTip[1]) / 2];

  // Chevron size ≈ gap width so it fills the lane
  const gapWidth = Math.sqrt(dist2(pairs[0].ob, pairs[0].ib));
  const chevronSize = Math.max(2, gapWidth * 0.20);

  return (
    <g style={{ pointerEvents: "none" }}>
      {pairs.map(({ ib, ob }, i) => {
        const from = [(ob[0] + ib[0]) / 2, (ob[1] + ib[1]) / 2];
        return (
          <ArrowStream
            key={i}
            from={from}
            to={tipMid}
            color={color}
            chevronSize={chevronSize}
          />
        );
      })}
    </g>
  );
}

/* ── main component ── */
function LudoTrianglePlayerBox({
  numberWiseColor,
  triangleCoordsStringArray,
  circleCoordinates,
  triangleInnerCordsArray,
  isActive,
}) {
  const boardCenter = useMemo(() => {
    if (!triangleInnerCordsArray?.length) return [400, 400];
    const cs = triangleInnerCordsArray.map(parseCentroid);
    return [
      cs.reduce((s, c) => s + c[0], 0) / cs.length,
      cs.reduce((s, c) => s + c[1], 0) / cs.length,
    ];
  }, [triangleInnerCordsArray]);

  const activeColor = isActive != null ? numberWiseColor[isActive] : null;

  return (
    <>
      {triangleCoordsStringArray?.map((cord, idx) => (
        <polygon key={idx} points={cord} fill="white" stroke="black" strokeWidth="1" />
      ))}

      {triangleInnerCordsArray?.map((cord, idx) => (
        <polygon
          key={idx}
          points={cord}
          fill={numberWiseColor[idx]}
          stroke="black"
          strokeWidth="1"
          className={isActive === idx ? "activePlayerPulse" : ""}
          style={{ "--player-color": numberWiseColor[idx] }}
        />
      ))}

      {circleCoordinates?.map((cord, i) =>
        cord.map(
          (point, idx) =>
            idx === 3 && (
              <circle key={idx} cx={point[0]} cy={point[1]} r={30} fill="white" />
            ),
        ),
      )}

      {isActive != null &&
        triangleCoordsStringArray?.[isActive] &&
        triangleInnerCordsArray?.[isActive] && (
          <TurnArrows
            outerCordStr={triangleCoordsStringArray[isActive]}
            innerCordStr={triangleInnerCordsArray[isActive]}
            boardCenter={boardCenter}
            color={activeColor}
          />
        )}
    </>
  );
}

export default memo(LudoTrianglePlayerBox);
